// The first payment question as a window, for the submit paths that are a
// single click: the deal page's Advance, Release to Robert, the pipeline stage
// dropdown, Roof Master's status and the admin override (Oct 21, 2026).
//
// useBuildSubmitGate().run(req, doUpdate) wraps the caller's own stage write:
//   - not a submission (see crossesBuildSubmission): just runs it;
//   - first payment cleared: runs it with no window;
//   - otherwise opens the window; Confirm records the plan (when one is
//     needed and it differs from the one on file), then runs the write.
// If the database still refuses (its count of the money differs from the
// window's), the window asks for the plan anyway. Resolves null on Cancel.

import { useCallback, useRef, useState, type ReactNode } from "react";
import { useAuth } from "../../context/AuthContext";
import { formatSupabaseError } from "../../lib/formatSupabaseError";
import {
  choiceIsComplete, crossesBuildSubmission, isPlanGateError, recordBuildPaymentPlan, sameAsOnFile,
  type BuildPaymentPlan, type FirstPaymentStatus, type PaymentPlanKey, type PlanChoice, type PlanSource,
} from "../../lib/buildPaymentPlan";
import BuildPaymentPlanPicker, { loadBuildPaymentSnapshot, planNeeded, type BuildPaymentSnapshot } from "./BuildPaymentPlanPicker";

export interface GateRequest {
  dealId: string;
  homeowner?: string | null;
  fromStage: string | null | undefined;
  toStage: string;
  source: PlanSource;
  /** The confirm button, e.g. "Submit build". */
  actionLabel?: string;
  title?: string;
}

export interface GatePass {
  plan: PaymentPlanKey | null;
  note: string | null;
  /** The plan row used (new or already on file). */
  recorded: BuildPaymentPlan | null;
  status: FirstPaymentStatus | null;
}

const NO_PLAN: GatePass = { plan: null, note: null, recorded: null, status: null };

/** The plan for Chad's "Build approval needed" text (notifyChadOnBuildApproval). */
export function passToNotifyPlan(p: GatePass | null | undefined) {
  if (!p?.plan) return null;
  return { plan: p.plan, note: p.note, owed: p.status?.owed ?? null, label: p.status?.label ?? "first payment" };
}

/** Record the plan when one is needed (and new), then run the stage write.
 *  Throws the database's refusal untouched so the caller can ask again. */
export async function submitWithPlan(o: {
  req: Pick<GateRequest, "dealId" | "fromStage" | "toStage" | "source">;
  snap: BuildPaymentSnapshot;
  choice: PlanChoice;
  forcePlan: boolean;
  actorId: string | null | undefined;
  doUpdate: () => Promise<void>;
}): Promise<GatePass> {
  const need = planNeeded(o.snap, o.forcePlan);
  let recorded: BuildPaymentPlan | null = null;
  if (need) {
    if (!choiceIsComplete(o.choice)) {
      throw new Error(o.choice.plan === "other" ? "Type the reason for the Other plan." : "Pick how the first payment will be collected.");
    }
    recorded = sameAsOnFile(o.choice, o.snap.onFile)
      ? o.snap.onFile
      : await recordBuildPaymentPlan({
          dealId: o.req.dealId, choice: o.choice, status: o.snap.status,
          stageFrom: o.req.fromStage, stageTo: o.req.toStage, source: o.req.source, actorId: o.actorId,
        });
  }
  await o.doUpdate();
  return {
    plan: need ? (o.choice.plan || null) as PaymentPlanKey | null : null,
    note: need ? (o.choice.note.trim() || null) : null,
    recorded,
    status: o.snap.status,
  };
}

interface Open {
  req: GateRequest;
  snap: BuildPaymentSnapshot;
  doUpdate: () => Promise<void>;
  resolve: (p: GatePass | null) => void;
  force: boolean;
}

export function useBuildSubmitGate(): {
  run: (req: GateRequest, doUpdate: () => Promise<void>) => Promise<GatePass | null>;
  element: ReactNode;
} {
  const { member } = useAuth();
  const [open, setOpen] = useState<Open | null>(null);
  const openRef = useRef<Open | null>(null);
  openRef.current = open;

  const run = useCallback(async (req: GateRequest, doUpdate: () => Promise<void>): Promise<GatePass | null> => {
    if (!crossesBuildSubmission(req.fromStage, req.toStage)) {
      await doUpdate();
      return NO_PLAN;
    }
    const snap = await loadBuildPaymentSnapshot(req.dealId);
    if (snap.status.isCleared) {
      try {
        await doUpdate();
        return { ...NO_PLAN, status: snap.status };
      } catch (e) {
        if (!isPlanGateError(e)) throw e;
        return new Promise<GatePass | null>((resolve) => setOpen({ req, snap, doUpdate, resolve, force: true }));
      }
    }
    return new Promise<GatePass | null>((resolve) => setOpen({ req, snap, doUpdate, resolve, force: false }));
  }, []);

  const finish = useCallback((p: GatePass | null) => {
    const o = openRef.current;
    setOpen(null);
    o?.resolve(p);
  }, []);

  const element = open ? (
    <BuildPaymentPlanModal
      key={open.req.dealId + open.req.toStage}
      open={open}
      actorId={member?.id ?? null}
      onForce={() => setOpen((o) => (o ? { ...o, force: true } : o))}
      onDone={finish}
    />
  ) : null;

  return { run, element };
}

function BuildPaymentPlanModal({ open, actorId, onForce, onDone }: {
  open: Open;
  actorId: string | null;
  onForce: () => void;
  onDone: (p: GatePass | null) => void;
}) {
  const { req, snap, force } = open;
  const [choice, setChoice] = useState<PlanChoice>(
    snap.onFile ? { plan: snap.onFile.plan, note: snap.onFile.note ?? "" } : { plan: "", note: "" },
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const need = planNeeded(snap, force);
  const canConfirm = !busy && (!need || choiceIsComplete(choice));

  async function confirm() {
    setBusy(true);
    setErr(null);
    try {
      const pass = await submitWithPlan({ req, snap, choice, forcePlan: force, actorId, doUpdate: open.doUpdate });
      onDone(pass);
    } catch (e) {
      if (isPlanGateError(e) && !force) {
        onForce();
        setErr("The server doesn't count the first payment as in yet. Pick how it will be collected.");
      } else {
        setErr(formatSupabaseError(e));
      }
    } finally {
      setBusy(false);
    }
  }

  const close = () => { if (!busy) onDone(null); };
  const title = req.title ?? "Submit build";

  return (
    // Clicks stop here: the window can sit inside a clickable card (the pipeline).
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 sm:p-4" onClick={(e) => { e.stopPropagation(); close(); }} role="dialog" aria-modal="true" aria-label={title}>
      <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-xl shadow-xl flex flex-col max-h-[92vh]" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-bardin-ink">{title}</h2>
            <p className="text-sm text-slate-500 mt-0.5 truncate">
              {req.homeowner ?? (snap.deal.homeowner_name as string | null) ?? "(no name)"}
              {snap.deal.property_street ? ` · ${String(snap.deal.property_street)}` : ""}
            </p>
          </div>
          <button onClick={close} aria-label="Close" className="text-slate-400 hover:text-slate-600 text-xl leading-none">×</button>
        </div>
        <div className="px-5 py-4 overflow-y-auto space-y-3">
          <BuildPaymentPlanPicker snap={snap} value={choice} onChange={setChoice} forcePlan={force} />
          {err && <div className="bg-red-50 text-red-700 text-sm p-2 rounded">{err}</div>}
        </div>
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex justify-end gap-2 rounded-b-xl">
          <button onClick={close} disabled={busy} className="px-3 py-1.5 rounded text-sm text-slate-600 hover:text-slate-800 disabled:opacity-50">Cancel</button>
          <button onClick={confirm} disabled={!canConfirm} className="px-4 py-1.5 rounded bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50">
            {busy ? "Saving…" : req.actionLabel ?? "Submit build"}
          </button>
        </div>
      </div>
    </div>
  );
}
